/**
 * Base64 Utility Functions
 * Handles base64 data conversion for document viewing
 */

/**
 * Converts base64 data to a blob URL for display
 * @param base64Data - The base64 encoded data (with or without data URL prefix)
 * @param mimeType - The MIME type of the file
 * @returns Blob URL that can be used in img src or object data
 */
export const convertBase64ToBlobUrl = (base64Data: string, mimeType: string): string => {
    try {
        // Remove data URL prefix if present
        const base64Content = base64Data.startsWith('data:')
            ? base64Data.split(',')[1]
            : base64Data;

        // Convert base64 to binary
        const binaryString = atob(base64Content);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob
        const blob = new Blob([bytes], { type: mimeType });

        // Create and return blob URL
        return URL.createObjectURL(blob);
    } catch (error) {
        console.error('Error converting base64 to blob URL:', error);
        throw new Error('Failed to convert base64 data to viewable format');
    }
};

/**
 * Converts blob to base64 data URL
 * @param blob - The blob to convert
 * @returns Promise that resolves to base64 data URL
 */
export const convertBlobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Revokes a blob URL to free memory
 * @param blobUrl - The blob URL to revoke
 */
export const revokeBlobUrl = (blobUrl: string): void => {
    if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
    }
};

/**
 * Formats file size in human readable format
 * @param bytes - Size in bytes
 * @returns Formatted string (e.g., "1.2 MB", "512 KB")
 */
export const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

/**
 * Gets file extension from MIME type
 * @param mimeType - MIME type (e.g., "image/jpeg", "application/pdf")
 * @returns File extension (e.g., "JPEG", "PDF")
 */
export const getFileExtensionFromMimeType = (mimeType: string): string => {
    const mimeMap: Record<string, string> = {
        'image/jpeg': 'JPEG',
        'image/jpg': 'JPG',
        'image/png': 'PNG',
        'image/gif': 'GIF',
        'image/bmp': 'BMP',
        'image/webp': 'WebP',
        'image/svg+xml': 'SVG',
        'application/pdf': 'PDF',
        'text/plain': 'TXT',
        'application/msword': 'DOC',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX'
    };

    return mimeMap[mimeType] || mimeType.split('/')[1]?.toUpperCase() || 'Unknown';
};

/**
 * Determines if a file type supports inline viewing
 * @param mimeType - MIME type of the file
 * @returns True if the file can be displayed inline
 */
export const isViewableInline = (mimeType: string): boolean => {
    const viewableTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/bmp',
        'image/webp',
        'application/pdf'
    ];

    return viewableTypes.includes(mimeType);
};

/**
 * Determines if a file is an image
 * @param mimeType - MIME type of the file
 * @returns True if the file is an image
 */
export const isImageFile = (mimeType: string): boolean => {
    return mimeType.startsWith('image/');
};

/**
 * Determines if a file is a PDF
 * @param mimeType - MIME type of the file
 * @returns True if the file is a PDF
 */
export const isPdfFile = (mimeType: string): boolean => {
    return mimeType === 'application/pdf';
};